import type { Nullable } from '@iheartjane/types';
import { useCallback, useMemo } from 'react';

import { useSearchContext } from '@jane/kiosk/providers/lib/SearchProvider';
import type { ProductSort } from '@jane/kiosk/util';
import { makeDefaultWithCustomLabel, makeSortOptions } from '@jane/kiosk/util';
import type { ArrayFilterValue, CurrentSort } from '@jane/search/data-access';
import { makeCurrentSort } from '@jane/search/data-access';
import {
  buildOptionalFilterString,
  mapCurrentSortToSearchSort,
  updateSortSuffix,
} from '@jane/search/util';
import type { Store } from '@jane/shared/types';

export interface ProductSortArgs {
  defaultSortName: Nullable<string>;
  hasThcProducts?: boolean;
  optionalFilters?: Store['optional_filters'];
  showAlphabeticalSort?: boolean;
  store: Store;
}

export const useProductSort = ({
  defaultSortName,
  hasThcProducts = false,
  showAlphabeticalSort = true,
  store,
}: ProductSortArgs): ProductSort => {
  const { searchState, setSearchState } = useSearchContext();

  const setCurrentSort = useCallback(
    (newSort?: CurrentSort) => {
      setSearchState({ currentSort: newSort });
    },
    [setSearchState]
  );

  const { optional_filters: optionalFilters } = store || {};

  const optionalFiltersString = optionalFilters
    ? Object.entries(optionalFilters).reduce(buildOptionalFilterString, '')
    : undefined;

  const sortOptions = useMemo(
    () =>
      makeSortOptions({
        defaultOption: makeDefaultWithCustomLabel(defaultSortName),
        hasOptionalFilters: !!optionalFiltersString,
        showAlphabeticalSort,
        showThcOptions: hasThcProducts,
      }),
    [
      defaultSortName,
      hasThcProducts,
      optionalFiltersString,
      showAlphabeticalSort,
    ]
  );

  const currentSort =
    searchState.currentSort || makeCurrentSort(sortOptions[0]);

  const weightFilters =
    (searchState.filters?.available_weights as ArrayFilterValue[]) || [];

  const searchSort = useMemo(() => {
    const sortWithWeights = {
      ...currentSort,
      suffix: updateSortSuffix(currentSort.suffix, weightFilters),
    };
    return mapCurrentSortToSearchSort(sortWithWeights);
  }, [currentSort, weightFilters]);

  return {
    currentSort,
    optionalFiltersString,
    searchSort,
    setCurrentSort,
    sortOptions,
  };
};
